import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import NeoModal from "../../../../components/Common/NeoModal";
import NeoInput from "../../../../components/Common/NeoInput";
import NeoButton from "../../../../components/Common/NeoButton";
import { listTenants } from "../../../../services/platform_admin_service";
import { TICKET_CATEGORIES, TICKET_PRIORITIES, TICKET_CHANNELS } from "../constants";

const TicketFormModal = ({
    isOpen,
    toggle,
    formTicket,
    onChangeField,
    onSave
}) => {
    const [tenants, setTenants] = useState([]);
    const [loadingTenants, setLoadingTenants] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Carrega academias reais ao abrir o modal
    useEffect(() => {
        if (isOpen && tenants.length === 0) {
            const fetchTenants = async () => {
                try {
                    setLoadingTenants(true);
                    const result = await listTenants({ page: 1, pageSize: 100 });
                    setTenants(result.items || []);
                } catch (err) {
                    console.error("[TicketFormModal] Erro ao carregar academias:", err);
                }finally {
                    setLoadingTenants(false);
                }
            };
            fetchTenants();
        }
    }, [isOpen, tenants.length]);

    const handleTenantChange = (tenantId) => {
        const tenant = tenants.find(t => t.id === tenantId);
        onChangeField("idTenant", tenantId);
        onChangeField("tenantName", tenant?.name || "");
        // Preenche contato do owner se disponível
        if (tenant?.owner) {
            if (!formTicket.contactName) onChangeField("contactName", tenant.owner.fullName || "");
            if (!formTicket.contactEmail) onChangeField("contactEmail", tenant.owner.email || "");
        }
        if (tenant?.phone && !formTicket.contactPhone) {
            onChangeField("contactPhone", tenant.phone);
        }
    };

    const handleSubmit = async () => {
        if (!formTicket.title || !formTicket.description) return;
        try {
            setSubmitting(true);
            await onSave();
        } finally {
            setSubmitting(false);
        }
    };

    const isValid = formTicket.title && formTicket.description;

    const tenantOptions = [
        { value: "", label: loadingTenants ? "Carregando academias..." : "Selecione uma academia..." },
        ...tenants.map(t => ({ value: t.id, label: t.name }))
    ];

    const footer = (
        <>
            <NeoButton variant="secondary" onClick={toggle} disabled={submitting}>
                Cancelar
            </NeoButton>
            <NeoButton variant="primary" onClick={handleSubmit} disabled={!isValid || submitting}>
                {submitting ? "Salvando..." : "Registrar Chamado"}
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            title="Abrir Chamado de Suporte"
            subtitle="Registre um novo chamado para acompanhamento e resolução da equipe interna."
            size="lg"
            footerActions={footer}
        >
            {/* SEÇÃO 1: Identificação */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3" style={{ letterSpacing: "0.5px" }}>
                Identificação do Chamado
            </h6>

            <div className="mb-3">
                <NeoInput
                    label="Título / Assunto *"
                    placeholder="Ex: Erro ao gerar relatório de frequência mensal"
                    value={formTicket.title}
                    onChange={(e) => onChangeField("title", e.target.value)}
                />
            </div>

            <Row className="g-3 mb-3">
                <Col md={6}>
                    <NeoInput
                        label="Academia / Cliente"
                        type="select"
                        value={formTicket.idTenant}
                        onChange={(e) => handleTenantChange(e.target.value)}
                        options={tenantOptions}
                    />
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Canal de Origem *"
                        type="select"
                        value={formTicket.channel}
                        onChange={(e) => onChangeField("channel", e.target.value)}
                        options={TICKET_CHANNELS}
                    />
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col md={6}>
                    <NeoInput
                        label="Categoria *"
                        type="select"
                        value={formTicket.category}
                        onChange={(e) => onChangeField("category", e.target.value)}
                        options={TICKET_CATEGORIES}
                    />
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Prioridade *"
                        type="select"
                        value={formTicket.priority}
                        onChange={(e) => onChangeField("priority", e.target.value)}
                        options={TICKET_PRIORITIES}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 2: Contato */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 mt-3" style={{ letterSpacing: "0.5px" }}>
                Contato do Solicitante
            </h6>

            <Row className="g-3 mb-4">
                <Col md={4}>
                    <NeoInput
                        label="Nome do Contato"
                        placeholder="Ex: João Silva"
                        value={formTicket.contactName}
                        onChange={(e) => onChangeField("contactName", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="WhatsApp / Telefone"
                        placeholder="(00) 90000-0000"
                        value={formTicket.contactPhone}
                        onChange={(e) => onChangeField("contactPhone", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <NeoInput
                        label="E-mail"
                        type="email"
                        placeholder="contato@academia.com"
                        value={formTicket.contactEmail}
                        onChange={(e) => onChangeField("contactEmail", e.target.value)}
                    />
                </Col>
            </Row>

            <hr />

            {/* SEÇÃO 3: Detalhes */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 mt-3" style={{ letterSpacing: "0.5px" }}>
                Detalhes do Problema
            </h6>

            <div>
                <NeoInput
                    label="Descrição Detalhada *"
                    type="textarea"
                    rows={5}
                    placeholder="Descreva o problema com o máximo de detalhes possível: o que o cliente estava fazendo, qual erro apareceu, qual a frequência do problema, etc."
                    value={formTicket.description}
                    onChange={(e) => onChangeField("description", e.target.value)}
                />
            </div>
        </NeoModal>
    );
};

TicketFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    formTicket: PropTypes.object.isRequired,
    onChangeField: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default TicketFormModal;
